import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/react';
import { Notes } from '@codametrix/shared-views';
import React from 'react';

import type { NotesData } from '../stateMappers/caseRunToCodingScreenState';
import { useNoteInteraction } from './noteInteractionContext';

function noteKey(note: CMxCommonApp.DecoratedNote): string {
  return String(note.document_id);
}

export interface SourceDocumentModalProps {
  readonly notes: NotesData['notes'];
}

/**
 * The source-document modal (AC3/AC7/AC11) -- opened by either the `NoteViewer` header
 * icon or a `NoteNav` item's icon (both call `openSourceModal(key)`), rendering the SAME note
 * content the main viewer already renders, through the SAME shared-views `<Notes>` component --
 * `Notes` itself picks between `NoteFrame` (`'text/html'`), `PDFFrame` (`'application/pdf'`), or a
 * plain-text/segmented fallback by `filetype`; passing it a single-note array here reuses that exact
 * dispatch rather than re-implementing it, so the modal can never drift out of sync with how the
 * main viewer renders the identical note.
 *
 * `isOpen` is derived from `sourceModalNoteKey` resolving to a real note in `notes`; `onClose` calls
 * `closeSourceModal()`, which clears ONLY `sourceModalNoteKey` -- `activeNoteKey` (which note the
 * nav/viewer show) is completely untouched, so closing the modal restores the exact prior note/nav
 * state (AC11). Read-only, same as the main viewer (`disabled` is always `true`).
 */
export const SourceDocumentModal: React.FC<SourceDocumentModalProps> = ({
  notes
}) => {
  const { sourceModalNoteKey, closeSourceModal } = useNoteInteraction();
  const note = notes.find((n) => noteKey(n) === sourceModalNoteKey);
  const isOpen = sourceModalNoteKey !== undefined && note !== undefined;

  return (
    <Modal isOpen={isOpen} onClose={closeSourceModal} size="4xl">
      <ModalOverlay />
      <ModalContent data-testid="source-document-modal">
        <ModalHeader>{note?.filename ?? 'Document'}</ModalHeader>
        <ModalCloseButton aria-label="Close source document" />
        <ModalBody>
          <Notes notes={note ? [note] : []} disabled />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
